import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, ViewEncapsulation } from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { firstValueFrom, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { BookmarkService } from 'src/app/service/data/bookmark.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Themes } from '../../enums/enums'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcDeleteDialogComponent } from '../ddc-delete-dialog/ddc-delete-dialog.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'
@Component({
  selector: 'ddc-edit-one-dialog',
  templateUrl: './ddc-edit-one-dialog.component.html',
  styleUrl: './ddc-edit-one-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    DdcTextGradientComponent,
    DdcButtonSOneComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcEditOneDialogComponent {
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string

  private themeSubscription: Subscription

  _submitBtnText = 'Edit'
  _cardTitle = ''
  _titleMaxLength: number = 100
  _descriptionMaxLength: number = 10000

  _selectedLngHasArticle: boolean = false

  _isDic: boolean = false
  _isCollection: boolean = false
  _titleText: string = ''

  _isEditBtnLoading = false
  _editError: string = ''
  _editedField: string = ''
  _deletedField: boolean = false

  _element: { id: number; title: string; description: string }

  _form = new FormGroup({
    _title: new FormControl('', [Validators.required, Validators.maxLength(this._titleMaxLength)]),

    _description: new FormControl(null, [Validators.maxLength(this._descriptionMaxLength)]),
  })

  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  partOfSpeeches: any

  constructor(
    private logger: DdcLoggerService,
    private fb: FormBuilder,
    public themeService: ThemeService,
    private dic_service: DictionaryService,
    private bookmark_service: BookmarkService,
    public dialog: MatDialog,
    private editDialogRef: MatDialogRef<DdcEditOneDialogComponent>,
    private deleteDialogRef: MatDialogRef<DdcDeleteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    if (data.element.type == 'collection') {
      this._cardTitle = 'Collection'
      this._titleText = 'Name'
      this._isCollection = true
      this._element = {
        id: data.element.elem.id,
        title: data.element.elem.name,
        description: '',
      }
      this._form = this.fb.group({
        _title: [this._element.title],
        _description: '',
      })
    }
  }

  get title(): any {
    return this._form.get('_title')
  }

  get description(): any {
    return this._form.get('_description')
  }

  isFormValid(): boolean {
    const title = this._form.get('_title').valid
    const desc = this._form.get('_description').valid

    if (title && desc) {
      return true
    }

    return false
  }

  async editDic(): Promise<boolean> {
    this.logger.info('DdcEditOneDialogComponent: editDic()')

    try {
      const editedDic = {
        id: this._element.id,
        title: this._form.value._title,
        description: this._form.value._description,
      }

      const res: any = await firstValueFrom(this.dic_service.edit_dic(editedDic))

      this._isEditBtnLoading = false
      this._submitBtnText = 'Edit'

      if (res.success) {
        this._editError = ''
        this._editedField = res.res.title
        return true
      } else {
        this.logger.error('DdcEditOneDialogComponent: editDic() | failed: res:', res)
        this._editError = res.res
        this._editedField = ''
        return false
      }
    } catch (error) {
      this.logger.error('DdcEditOneDialogComponent: editDic() | error:', error)
      return false
    }
  }

  async editColl(): Promise<boolean> {
    this.logger.info('DdcEditOneDialogComponent: editColl()')
    try {
      const editedColl = {
        id: this._element.id,
        name: this._form.value._title,
      }
      this.logger.info('DdcEditOneDialogComponent: editColl() | editedColl:', editedColl)
      const res: any = await firstValueFrom(this.bookmark_service.edit_collection(editedColl))

      this._isEditBtnLoading = false
      this._submitBtnText = 'Edit'

      if (res.success) {
        this._editError = ''
        this._editedField = res.res.name
        return true
      } else {
        this.logger.error('DdcEditOneDialogComponent: editColl() | failed: res:', res)
        this._editError = res.res
        this._editedField = ''
        return false
      }
    } catch (error) {
      this.logger.error('DdcEditOneDialogComponent: editColl() | error:', error)
      return false
    }
  }

  async edit(): Promise<void> {
    this.logger.info('DdcEditOneDialogComponent: edit()')
    if (!this.isFormValid()) return

    try {
      if (this._isDic) {
        const handledDic = await this.editDic()
      } else if (this._isCollection) {
        const handledColl = await this.editColl()
      }
    } catch (error) {
      this.logger.error('DdcEditOneDialogComponent: edit() | error:', error)
    }
  }

  closeDialog(): void {
    const closeMsg = this._editedField ? 'edited' : 'close'
    this.editDialogRef.close(closeMsg)
  }

  openDeleteDialog(): void {
    this.logger.info('DdcEditOneDialogComponent: openDeleteDialog()')
    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        type: this._isDic ? 'Dictionary' : 'Collection',
        element: this._element,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'delete-element') {
        this.dic_service.delete_dic(this._element.id).subscribe((res: any) => {
          if (res.success) {
            this._editError = ''
            this._editedField = this._element.title
            this._deletedField = true
          } else {
            this.logger.error(
              'DdcEditOneDialogComponent: openDeleteDialog() | dialogRef.afterClosed() | delete_dic: failed: res:',
              res,
            )
            this._editError = res.res
            this._editedField = ''
            this._deletedField = true
          }
          this._isEditBtnLoading = false
          this._submitBtnText = 'Edit'
        })
      } else if (result === 'delete-collection') {
        this.bookmark_service.delete_collection(this._element.id).subscribe((res: any) => {
          if (res.success) {
            this._editError = ''
            this._editedField = this._element.title
            this._deletedField = true
          } else {
            this.logger.error(
              'DdcEditOneDialogComponent: openDeleteDialog() | dialogRef.afterClosed() | delete_collection: failed: res:',
              res,
            )
            this._editError = res.res
            this._editedField = ''
            this._deletedField = true
          }
          this._isEditBtnLoading = false
          this._submitBtnText = 'Edit'
        })
      }
    })
  }
}
